/**
 * InventoryItem class to standardize inventory item objects across the application
 * Ensures consistent structure and behavior for inventory data
 */
class InventoryItem {
  constructor(data = {}) {
    this.id = data._id || data.id || '';
    this.userId = data.userId || data.user || '';
    this.cardId = typeof data.card === 'string' ? data.card : (data.card?.id || data.card?._id || '');
    this.productId = typeof data.product === 'string' ? data.product : (data.product?.id || data.product?._id || '');
    this.insertSetId = typeof data.insertSet === 'string' ? data.insertSet : (data.insertSet?.id || data.insertSet?._id || '');
    this.quantity = data.quantity || 0;
    this.condition = data.condition || 'Near Mint';
    this.notes = data.notes || '';
    this.forTrade = data.forTrade || false;
    this.forSale = data.forSale || false;
    this.createdAt = data.createdAt ? new Date(data.createdAt) : null;
    this.updatedAt = data.updatedAt ? new Date(data.updatedAt) : null;
    
    // Keep the original _id for MongoDB compatibility
    this._id = data._id || '';
    
    // Store the full objects if provided
    this.card = data.card && typeof data.card === 'object' ? data.card : null;
    this.product = data.product && typeof data.product === 'object' ? data.product : null;
    this.insertSet = data.insertSet && typeof data.insertSet === 'object' ? data.insertSet : null;
  }

  /**
   * Convert this object back to API format for sending to server
   */
  toApiFormat() {
    // Don't include _id if it's empty to let MongoDB generate one
    const data = {
      user: this.userId,
      card: this.cardId,
      product: this.productId,
      insertSet: this.insertSetId,
      quantity: this.quantity,
      condition: this.condition,
      notes: this.notes,
      forTrade: this.forTrade,
      forSale: this.forSale
    };
    
    // Only include _id if it actually has a value
    if (this.id) {
      data._id = this.id;
    }
    
    return data;
  }

  /**
   * Create an InventoryItem object from API response data
   * @param {Object} data - API response data
   * @returns {InventoryItem} - InventoryItem instance
   */
  static fromApiResponse(data) {
    // Handle nested inventoryItem object in response
    const inventoryData = data.inventoryItem || data;
    return new InventoryItem(inventoryData);
  }
  
  /**
   * Create an array of InventoryItem objects from API response data
   * @param {Array} dataArray - Array of inventory item data objects
   * @returns {Array<InventoryItem>} - Array of InventoryItem instances
   */
  static fromApiResponseArray(dataArray) {
    if (!Array.isArray(dataArray)) {
      console.error('Expected array for InventoryItem.fromApiResponseArray, got:', dataArray);
      return [];
    }
    
    return dataArray.map(item => new InventoryItem(item));
  }
}

// Use CommonJS exports for Node.js backend compatibility
module.exports = InventoryItem;
// Also support ES modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports.default = InventoryItem;
}